// A tiny wrapper around fetch(), borrowed from
// https://kentcdodds.com/blog/replace-axios-with-a-simple-custom-fetch-wrapper


type RequestMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
interface ClientConfig extends RequestInit {
    headers?: { [key: string]: string };
    method?: RequestMethod;
    token?: string;
    body?: any;
}

const parseResponseBody = async (response: Response) => {
    const text = await response.text();
    if (!text) {
        return null;
    }

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
};

export async function client(endpoint: string, { body, token, ...customConfig }: ClientConfig = {}) {
    const headers = {
        'Content-Type': 'application/json',
        'X-App-Version': 'WEB',
        // Add the Bearer Token if provided
        ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    const config: RequestInit = {
        method: body ? 'POST' : 'GET',
        ...customConfig,
        headers: {
            ...headers,
            ...customConfig.headers,
        },
    };

    if (body) {
        config.body = JSON.stringify(body);
    }

    let data;
    try {
        const response = await fetch(endpoint, config);
        data = await parseResponseBody(response);
        if (response.ok) {
            // Return a result object similar to Axios
            return {
                status: response.status,
                data,
                headers: response.headers,
                url: response.url,
            };
        }
        return Promise.reject({
            status: response.status,
            statusText: response.statusText,
            data,
            message:
                (data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
                    ? data.message
                    : response.statusText) || 'Request failed',
        });
    } catch (err: any) {
        console.log("ERROR API REQUEST " + config?.method + " " + endpoint);
        if (typeof err === 'string') {
            return Promise.reject({ message: err, data });
        }
        return Promise.reject(err?.message ? err : { message: err?.message ?? 'Request failed', data });
    }
}

client.get = function (endpoint: string, customConfig = {}) {
    return client(endpoint, { ...customConfig, method: 'GET' });
};

client.post = function (endpoint: string, body: any, customConfig = {}) {
    return client(endpoint, { ...customConfig, body });
};
